import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { workersData, mockEvaluations } from '../../../../mocks/workers';

const CATEGORIES = [
  { key: '顧客目線_rating', label: '顧客目線', description: '保護者・子どもの立場に立ち、ニーズを先読みした対応ができているか' },
  { key: '笑顔_rating', label: '笑顔', description: '子どもや保護者に対して自然で温かい笑顔で接することができているか' },
  { key: '園の方針_rating', label: '園の方針', description: '保育理念・独自ルール・衛生基準などを理解し遵守しているか' },
  { key: '事前準備_rating', label: '事前準備', description: '当日の業務内容や持ち物の確認、イレギュラー対応への備えができているか' },
  { key: '雰囲気_rating', label: '雰囲気', description: 'スタッフや子どもとの場の空気をよくする存在になっているか' },
  { key: '運資質_rating', label: '運資質', description: 'リーダー・運営側として将来的に活躍できそうなポテンシャルがあるか' },
  { key: '清潔感_rating', label: '清潔感', description: '身だしなみ・服装・衛生面への配慮がきちんとできているか' },
  { key: '態度_rating', label: '態度', description: '言葉遣い・礼儀・時間管理など勤務全般における態度が適切か' },
] as const;

const DIRECT_SPOT_BENEFITS_S = [
  { icon: '⭐', title: '正社員として直接雇用', desc: '待遇・条件は面談にてご相談。あなたに合ったキャリアを一緒に設計します。' },
  { icon: '🏆', title: '幹部候補としてのキャリアパス', desc: '園の未来を担うリーダーとして、成長の機会を全力でサポートします。' },
  { icon: '👥', title: '園の運営・方針決定への参画', desc: 'スタッフの声が届く環境。あなたのアイデアが園づくりに活かされます。' },
  { icon: '💛', title: '充実した福利厚生', desc: '子育て支援・研修制度・有給取得推進など、長く働ける環境を整えています。' },
];

const DIRECT_SPOT_BENEFITS_A = [
  { icon: '📅', title: 'タイムリーを介さず直接ご案内', desc: '弊園から直接シフトをご連絡。手数料なしで、より多くの報酬をお手元に。' },
  { icon: '⏰', title: 'あなたのペースで柔軟に', desc: 'ご都合に合わせたシフト調整が可能。無理なく続けられる働き方を実現。' },
  { icon: '🎧', title: '専任担当者が毎回サポート', desc: '初回から安心。担当スタッフが丁寧にフォローします。' },
  { icon: '🔼', title: '正社員登用の可能性', desc: '直接スポットから正社員へ。あなたの意欲次第でキャリアアップできます。' },
];

const DIRECT_SPOT_BENEFITS_S_ICONS = [
  { icon: 'ri-user-star-line', title: '正社員として直接雇用', desc: '待遇・条件は面談にてご相談。あなたに合ったキャリアを一緒に設計します。' },
  { icon: 'ri-trophy-line', title: '幹部候補としてのキャリアパス', desc: '園の未来を担うリーダーとして、成長の機会を全力でサポートします。' },
  { icon: 'ri-team-line', title: '園の運営・方針決定への参画', desc: 'スタッフの声が届く環境。あなたのアイデアが園づくりに活かされます。' },
  { icon: 'ri-heart-line', title: '充実した福利厚生', desc: '子育て支援・研修制度・有給取得推進など、長く働ける環境を整えています。' },
];

const DIRECT_SPOT_BENEFITS_A_ICONS = [
  { icon: 'ri-calendar-check-line', title: 'タイムリーを介さず直接ご案内', desc: '弊園から直接シフトをご連絡。手数料なしで、より多くの報酬をお手元に。' },
  { icon: 'ri-time-line', title: 'あなたのペースで柔軟に', desc: 'ご都合に合わせたシフト調整が可能。無理なく続けられる働き方を実現。' },
  { icon: 'ri-customer-service-2-line', title: '専任担当者が毎回サポート', desc: '初回から安心。担当スタッフが丁寧にフォローします。' },
  { icon: 'ri-arrow-up-circle-line', title: '正社員登用の可能性', desc: '直接スポットから正社員へ。あなたの意欲次第でキャリアアップできます。' },
];

const buildRadarPath = (
  values: number[],
  max: number,
  cx: number,
  cy: number,
  r: number
): string => {
  const n = values.length;
  return values
    .map((val, i) => {
      const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
      const ratio = Math.min(val / max, 1);
      const x = cx + r * ratio * Math.cos(angle);
      const y = cy + r * ratio * Math.sin(angle);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ') + ' Z';
};

const GenderSilhouette = ({ isFemale, size = 48 }: { isFemale: boolean; size?: number }) => (
  <i
    className={isFemale ? 'ri-user-smile-line' : 'ri-user-line'}
    style={{ fontSize: size, color: '#b45309', lineHeight: 1 }}
  ></i>
);

const RadarChart = ({ evaluations }: { evaluations: typeof mockEvaluations }) => {
  const cx = 200; const cy = 200; const r = 140;
  const n = CATEGORIES.length; const max = 10;
  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1.0];
  const recentEvals = [...evaluations]
    .sort((a, b) => new Date(a.work_date).getTime() - new Date(b.work_date).getTime())
    .slice(-5);
  const colors = ['#c8a96e', '#a8895a', '#8a6e48', '#d4b882', '#e0cc9a'];
  const strokeColors = ['#b8944a', '#c8a96e', '#a8895a', '#d4b882', '#e0cc9a'];

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      viewBox="0 0 400 420"
      style={{ display: 'block', margin: '0 auto', maxWidth: 360 }}
    >
      {gridLevels.map((level, li) => {
        const pts = CATEGORIES.map((_, i) => {
          const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
          const x = cx + r * level * Math.cos(angle);
          const y = cy + r * level * Math.sin(angle);
          return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
        }).join(' ') + ' Z';
        return <path key={li} d={pts} fill="none" stroke="#d4c4a8" strokeWidth="0.8" opacity="0.7" />;
      })}
      {CATEGORIES.map((_, i) => {
        const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
        const x = cx + r * Math.cos(angle);
        const y = cy + r * Math.sin(angle);
        return <line key={i} x1={cx} y1={cy} x2={x.toFixed(2)} y2={y.toFixed(2)} stroke="#d4c4a8" strokeWidth="0.8" opacity="0.7" />;
      })}
      {recentEvals.map((ev, idx) => {
        const values = CATEGORIES.map(cat => (ev as any)[cat.key] as number);
        const path = buildRadarPath(values, max, cx, cy, r);
        return (
          <path
            key={idx}
            d={path}
            fill={colors[idx % colors.length]}
            fillOpacity={(0.35 + idx * 0.08).toFixed(2)}
            stroke={strokeColors[idx % strokeColors.length]}
            strokeWidth="1.5"
          />
        );
      })}
      {CATEGORIES.map((cat, i) => {
        const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
        const labelR = r + 24;
        const x = cx + labelR * Math.cos(angle);
        const y = cy + labelR * Math.sin(angle);
        return (
          <text
            key={i}
            x={x.toFixed(2)}
            y={y.toFixed(2)}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="11"
            fill="#7a6a52"
            fontFamily="'Hiragino Kaku Gothic Pro','Meiryo',sans-serif"
          >
            {cat.label}
          </text>
        );
      })}
      <g transform="translate(0,390)">
        {recentEvals.map((ev, idx) => {
          const x = 10 + idx * 76;
          return (
            <g key={idx}>
              <rect x={x} y={0} width={10} height={10} fill={strokeColors[idx % strokeColors.length]} rx={2} />
              <text x={x + 14} y={9} fontSize="9" fill="#9a8a72" fontFamily="'Hiragino Kaku Gothic Pro','Meiryo',sans-serif">
                {ev.work_date}
              </text>
            </g>
          );
        })}
      </g>
    </svg>
  );
};

const buildRadarSvgString = (evaluations: typeof mockEvaluations): string => {
  const cx = 200; const cy = 200; const r = 140;
  const n = CATEGORIES.length; const max = 10;
  const gridLevels = [0.2, 0.4, 0.6, 0.8, 1.0];
  const recentEvals = [...evaluations]
    .sort((a, b) => new Date(a.work_date).getTime() - new Date(b.work_date).getTime())
    .slice(-5);
  const colors = ['#c8a96e', '#a8895a', '#8a6e48', '#d4b882', '#e0cc9a'];
  const strokeColors = ['#b8944a', '#c8a96e', '#a8895a', '#d4b882', '#e0cc9a'];

  const gridPaths = gridLevels.map(level => {
    const pts = CATEGORIES.map((_, i) => {
      const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
      const x = cx + r * level * Math.cos(angle);
      const y = cy + r * level * Math.sin(angle);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
    }).join(' ') + ' Z';
    return `<path d="${pts}" fill="none" stroke="#d4c4a8" stroke-width="0.8" opacity="0.7"/>`;
  }).join('');

  const axisLines = CATEGORIES.map((_, i) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    const x = cx + r * Math.cos(angle); const y = cy + r * Math.sin(angle);
    return `<line x1="${cx}" y1="${cy}" x2="${x.toFixed(2)}" y2="${y.toFixed(2)}" stroke="#d4c4a8" stroke-width="0.8" opacity="0.7"/>`;
  }).join('');

  const dataPolygons = recentEvals.map((ev, idx) => {
    const values = CATEGORIES.map(cat => (ev as any)[cat.key] as number);
    const path = buildRadarPath(values, max, cx, cy, r);
    return `<path d="${path}" fill="${colors[idx % colors.length]}" fill-opacity="${(0.35 + idx * 0.08).toFixed(2)}" stroke="${strokeColors[idx % strokeColors.length]}" stroke-width="1.5"/>`;
  }).join('');

  const labels = CATEGORIES.map((cat, i) => {
    const angle = (Math.PI * 2 * i) / n - Math.PI / 2;
    const labelR = r + 24;
    const x = cx + labelR * Math.cos(angle); const y = cy + labelR * Math.sin(angle);
    return `<text x="${x.toFixed(2)}" y="${y.toFixed(2)}" text-anchor="middle" dominant-baseline="middle" font-size="11" fill="#7a6a52" font-family="'Hiragino Kaku Gothic Pro','Meiryo',sans-serif">${cat.label}</text>`;
  }).join('');

  const legend = recentEvals.map((ev, idx) => {
    const x = 10 + idx * 76;
    return `<rect x="${x}" y="0" width="10" height="10" fill="${strokeColors[idx % strokeColors.length]}" rx="2"/><text x="${x + 14}" y="9" font-size="9" fill="#9a8a72" font-family="'Hiragino Kaku Gothic Pro','Meiryo',sans-serif">${ev.work_date}</text>`;
  }).join('');

  return `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="420" viewBox="0 0 400 420" style="display:block;margin:0 auto;max-width:360px;">${gridPaths}${axisLines}${dataPolygons}${labels}<g transform="translate(0,390)">${legend}</g></svg>`;
};

const generateHtmlEmail = (
  worker: typeof workersData[0],
  evaluations: typeof mockEvaluations,
  mainBodyText: string[],
  topComments: { comment: string; evaluator: string }[],
  benefits: typeof DIRECT_SPOT_BENEFITS_S | null,
  subject: string
): string => {
  const radarSvg = evaluations.length > 0 && worker.rank !== 'ブロック' ? buildRadarSvgString(evaluations) : '';

  const scoreRows = [...evaluations]
    .sort((a, b) => new Date(b.work_date).getTime() - new Date(a.work_date).getTime())
    .slice(0, 5)
    .map(ev => {
      const total = CATEGORIES.reduce((s, c) => s + ((ev as any)[c.key] || 0), 0);
      const cells = CATEGORIES.map(cat => `<td style="padding:6px 8px;text-align:center;color:#57534e;border-bottom:1px solid #f5f5f4;">${(ev as any)[cat.key]}</td>`).join('');
      return `<tr><td style="padding:6px 10px;color:#78716c;border-bottom:1px solid #f5f5f4;">${ev.work_date}</td>${cells}<td style="padding:6px 10px;text-align:center;font-weight:700;color:#b45309;border-bottom:1px solid #f5f5f4;">${total}</td></tr>`;
    }).join('');

  const scoreTableHeaders = CATEGORIES.map(cat => `<th style="padding:8px;text-align:center;color:#78716c;font-weight:500;font-size:11px;">${cat.label}</th>`).join('');

  const benefitsHtml = benefits ? `
    <div style="margin:28px 0;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:16px;">
        <div style="flex:1;height:1px;background:#e7e5e4;"></div>
        <span style="font-size:10px;color:#a8a29e;letter-spacing:0.1em;padding:0 8px;">${worker.rank === 'S' ? 'OFFER DETAILS' : 'DIRECT SPOT BENEFITS'}</span>
        <div style="flex:1;height:1px;background:#e7e5e4;"></div>
      </div>
      <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;">
        <tr>
          ${benefits.slice(0, 2).map(b => `
          <td width="50%" style="padding:4px;">
            <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:12px;padding:14px;">
              <div style="font-size:20px;margin-bottom:6px;">${b.icon}</div>
              <div style="font-size:12px;font-weight:700;color:#1c1917;margin-bottom:4px;">${b.title}</div>
              <div style="font-size:11px;color:#78716c;line-height:1.6;">${b.desc}</div>
            </div>
          </td>`).join('')}
        </tr>
        <tr>
          ${benefits.slice(2, 4).map(b => `
          <td width="50%" style="padding:4px;">
            <div style="background:#fffbeb;border:1px solid #fde68a;border-radius:12px;padding:14px;">
              <div style="font-size:20px;margin-bottom:6px;">${b.icon}</div>
              <div style="font-size:12px;font-weight:700;color:#1c1917;margin-bottom:4px;">${b.title}</div>
              <div style="font-size:11px;color:#78716c;line-height:1.6;">${b.desc}</div>
            </div>
          </td>`).join('')}
        </tr>
      </table>
    </div>` : '';

  const commentsHtml = topComments.length > 0 && worker.rank !== 'ブロック' ? `
    <div style="margin:28px 0;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:12px;">
        <div style="flex:1;height:1px;background:#e7e5e4;"></div>
        <span style="font-size:10px;color:#a8a29e;letter-spacing:0.1em;padding:0 8px;">STAFF VOICES</span>
        <div style="flex:1;height:1px;background:#e7e5e4;"></div>
      </div>
      <p style="font-size:11px;color:#a8a29e;text-align:center;margin:0 0 14px;">一緒に働いたきらりのスタッフが、こんなコメントを寄せています</p>
      <div style="background:#fafaf9;border-radius:16px;padding:16px;border:1px solid #f5f5f4;">
        ${topComments.map(c => `
        <div style="display:flex;align-items:flex-end;gap:10px;margin-bottom:14px;">
          <div style="width:34px;height:34px;border-radius:50%;background:#fde68a;display:flex;align-items:center;justify-content:center;font-size:13px;font-weight:700;color:#44403c;flex-shrink:0;border:1px solid #e7e5e4;">${c.evaluator.charAt(0)}</div>
          <div>
            <div style="font-size:10px;color:#a8a29e;margin-bottom:3px;">${c.evaluator}さん</div>
            <div style="background:#fff;border:1px solid #e7e5e4;border-radius:14px 14px 14px 2px;padding:10px 14px;font-size:12px;color:#44403c;line-height:1.6;box-shadow:0 1px 3px rgba(0,0,0,0.05);">「${c.comment}」</div>
          </div>
        </div>`).join('')}
      </div>
    </div>` : '';

  const chartHtml = radarSvg ? `
    <div style="margin:28px 0;">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:14px;">
        <div style="flex:1;height:1px;background:#e7e5e4;"></div>
        <span style="font-size:10px;color:#a8a29e;letter-spacing:0.1em;padding:0 8px;">EVALUATION RADAR CHART</span>
        <div style="flex:1;height:1px;background:#e7e5e4;"></div>
      </div>
      <div style="background:#fafaf9;border:1px solid #f5f5f4;border-radius:12px;padding:16px;">
        ${radarSvg}
      </div>
      <div style="margin-top:16px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
          <div style="flex:1;height:1px;background:#e7e5e4;"></div>
          <span style="font-size:10px;color:#a8a29e;letter-spacing:0.1em;padding:0 8px;">SCORE HISTORY</span>
          <div style="flex:1;height:1px;background:#e7e5e4;"></div>
        </div>
        <table width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;font-size:11px;border-radius:10px;overflow:hidden;border:1px solid #f5f5f4;">
          <thead>
            <tr style="background:#fafaf9;">
              <th style="padding:8px 10px;text-align:left;color:#78716c;font-weight:500;font-size:11px;">日付</th>
              ${scoreTableHeaders}
              <th style="padding:8px 10px;text-align:center;color:#b45309;font-weight:700;font-size:11px;">合計</th>
            </tr>
          </thead>
          <tbody>${scoreRows}</tbody>
        </table>
      </div>
    </div>` : '';

  const bodyParagraphs = mainBodyText.map(t => `<p style="font-size:14px;color:#44403c;line-height:1.8;margin:0 0 14px;">${t}</p>`).join('');

  return `<!DOCTYPE html>
<html lang="ja">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"><title>${subject}</title></head>
<body style="margin:0;padding:0;background:#f5f5f4;font-family:'Hiragino Kaku Gothic Pro','Meiryo','Yu Gothic',sans-serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background:#f5f5f4;padding:32px 16px;">
    <tr><td align="center">
      <table width="600" cellpadding="0" cellspacing="0" style="max-width:600px;width:100%;background:#ffffff;border-radius:16px;overflow:hidden;box-shadow:0 2px 12px rgba(0,0,0,0.08);">
        <tr>
          <td style="background:#fffbeb;padding:20px 32px;border-bottom:1px solid #fde68a;">
            <p style="margin:0;font-size:11px;color:#92400e;font-weight:600;letter-spacing:0.08em;">きらり保育園箕面　採用担当</p>
            <p style="margin:6px 0 0;font-size:16px;font-weight:700;color:#1c1917;">${subject}</p>
          </td>
        </tr>
        <tr>
          <td style="padding:32px 32px 8px;">
            <p style="font-size:15px;font-weight:700;color:#1c1917;margin:0 0 20px;">${worker.name} 様</p>
            ${bodyParagraphs}
          </td>
        </tr>
        <tr>
          <td style="padding:0 32px;">
            ${benefitsHtml}
            ${commentsHtml}
            ${chartHtml}
          </td>
        </tr>
        <tr>
          <td style="padding:8px 32px 24px;">
            <p style="font-size:14px;color:#44403c;line-height:1.8;margin:0 0 14px;">ご興味がございましたら、お気軽にご返信ください。<br>${worker.name}様のご参加を、スタッフ一同楽しみにしています。</p>
          </td>
        </tr>
        <tr>
          <td style="background:#fafaf9;padding:16px 32px;border-top:1px solid #f5f5f4;">
            <p style="margin:0;font-size:12px;color:#a8a29e;">きらり保育園箕面　採用担当</p>
            <p style="margin:2px 0 0;font-size:10px;color:#d6d3d1;">Kirari Nursery School Minoh — Recruitment</p>
          </td>
        </tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
};

const MockWorkerOfferPage = () => {
  const { id } = useParams<{ id: string }>();
  const [worker, setWorker] = useState<typeof workersData[0] | null>(null);
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');
  const [showSendInstructions, setShowSendInstructions] = useState(false);

  useEffect(() => {
    if (id) {
      const foundWorker = workersData.find(w => w.id === id);
      if (foundWorker) setWorker(foundWorker);
    }
  }, [id]);

  const getWorkerEvaluations = (workerId: string) =>
    mockEvaluations.filter(e => e.worker_id === workerId);

  const getTopComments = (workerId: string) => {
    const evals = mockEvaluations.filter(e => e.worker_id === workerId && e.overall_comment);
    const scored = evals.map(e => ({
      comment: e.overall_comment,
      evaluator: e.evaluator_name,
      score: CATEGORIES.reduce((sum, cat) => sum + ((e as any)[cat.key] || 0), 0),
    }));
    scored.sort((a, b) => b.score - a.score);
    return scored.slice(0, 3);
  };

  const getEmailSubject = (): string => {
    if (!worker) return '';
    switch (worker.rank) {
      case 'S': return `【特別オファー】きらり保育園より、${worker.name}様だけへのご招待`;
      case 'A': return `【直接スポットご招待】${worker.name}様の成長を、きらり保育園は見ています`;
      case 'B': return `【ご案内】${worker.name}様へ、きらり保育園からのお願い`;
      case 'ブロック': return '【ご連絡】今後のスポットワークについて';
      default: return '【案件のご案内】お仕事のお知らせ';
    }
  };

  const getMainBodyText = (): string[] => {
    if (!worker) return [];
    const name = worker.name;
    const score = worker.averageScore.toFixed(1);
    switch (worker.rank) {
      case 'S':
        return [
          `${name}様、突然のご連絡失礼いたします。きらり保育園箕面　採用担当です。`,
          `タイムリーを通じてご勤務いただいた期間、${name}様の評価スコアは平均 ${score}点。弊園でご勤務いただいた全スタッフの中でも、トップクラスの評価です。`,
          `きらり保育園では、タイムリーからお越しいただくスポットワーカーさんを「大切な仲間」として迎えています。スタッフ全員で見守り、応援してきました。`,
          `そして今、${name}様にお伝えしたいことがあります。タイムリーで来てくださる側から、きらり保育園でタイムリーさんを迎える側へ。そんなマインドチェンジをしていただきたいのです。子どもたちのために、仲間として、ともに園をつくっていく存在になってほしい。そんな思いを込めて、正社員・幹部候補としてのオファーをお届けします。`,
        ];
      case 'A':
        return [
          `いつもタイムリーを通じてきらり保育園にお越しいただき、ありがとうございます。きらり保育園箕面　採用担当です。`,
          `弊園でのご勤務を重ねるたびに、${name}様の評価は着実に上がっています。現在の平均スコアは ${score}点。`,
          `きらり保育園では、スポットワーカーさんを「外部の方」とは思っていません。シフトに入ってくださる日は、スタッフ全員で温かくお迎えし、子どもたちのためにともに動く「仲間」として接しています。`,
          `ここで一つ、${name}様にご提案があります。タイムリーで来てくださる側から、きらり保育園でタイムリーさんを迎える側へ。そんなマインドチェンジのきっかけとして、「直接スポット」という形でご参加いただけませんか。`,
          `「まずは一度、きらり保育園直接スポットで試してみる」だけで構いません。`,
        ];
      case 'B':
        return [
          `いつもタイムリーを通じてきらり保育園にお越しいただき、ありがとうございます。きらり保育園箕面　採用担当です。`,
          `${name}様がご勤務いただいた日、子どもたちはとても楽しそうでした。きらり保育園では、タイムリーからお越しいただくスポットワーカーさんのことを、スタッフ全員で温かく見守っています。`,
          `「今日来てくれた方、また来てほしいね」という声が、スタッフの間でよく上がっています。${name}様のことも、みんなで応援しています。`,
          `引き続き、タイムリーにてきらり保育園のシフトをご確認いただき、ぜひまたお越しいただけますと大変嬉しく思います。`,
        ];
      default:
        return [
          'いつもきらり保育園のスポットワークにご参加いただき、誠にありがとうございます。',
          '誠に恐れ入りますが、現時点では継続的なご勤務のご案内が難しい状況となっております。',
        ];
    }
  };

  const getBenefits = () => {
    if (!worker) return null;
    if (worker.rank === 'S') return DIRECT_SPOT_BENEFITS_S;
    if (worker.rank === 'A') return DIRECT_SPOT_BENEFITS_A;
    return null;
  };

  const getBenefitsIcons = () => {
    if (!worker) return null;
    if (worker.rank === 'S') return DIRECT_SPOT_BENEFITS_S_ICONS;
    if (worker.rank === 'A') return DIRECT_SPOT_BENEFITS_A_ICONS;
    return null;
  };

  const handleCopyHtml = async () => {
    if (!worker) return;
    const evals = getWorkerEvaluations(worker.id);
    const topComments = getTopComments(worker.id);
    const subject = getEmailSubject();
    const mainBodyText = getMainBodyText();
    const benefits = getBenefits();
    const htmlContent = generateHtmlEmail(worker, evals, mainBodyText, topComments, benefits, subject);
    try {
      await navigator.clipboard.writeText(htmlContent);
      setCopyStatus('copied');
    } catch {
      const ta = document.createElement('textarea');
      ta.value = htmlContent;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopyStatus('copied');
    }
    setTimeout(() => setCopyStatus('idle'), 5000);
  };

  const handleOpenMailer = () => {
    if (!worker) return;
    const subject = getEmailSubject();
    const mailto = `mailto:${worker.email}?subject=${encodeURIComponent(subject)}`;
    window.open(mailto, '_blank');
  };

  const handleDownloadForGmail = () => {
    if (!worker) return;
    const evals = getWorkerEvaluations(worker.id);
    const topComments = getTopComments(worker.id);
    const subject = getEmailSubject();
    const mainBodyText = getMainBodyText();
    const benefits = getBenefits();
    const htmlContent = generateHtmlEmail(worker, evals, mainBodyText, topComments, benefits, subject);
    const blob = new Blob([htmlContent], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `offer_${worker.name}_${new Date().toISOString().slice(0, 10)}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!worker) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="text-5xl mb-6">🔍</div>
          <h2 className="text-xl font-bold text-stone-700 mb-4">ワーカーが見つかりません</h2>
          <Link to="/mock-workers" className="inline-flex items-center gap-2 px-5 py-3 bg-amber-700 text-white rounded-lg hover:bg-amber-800 transition-colors whitespace-nowrap text-sm">
            <i className="ri-arrow-left-line"></i>
            モックワーカー一覧へ戻る
          </Link>
        </div>
      </div>
    );
  }

  const getRankBadgeStyle = (rank: string) => {
    switch (rank) {
      case 'S': return 'bg-amber-100 text-amber-800 border border-amber-300';
      case 'A': return 'bg-stone-100 text-stone-700 border border-stone-300';
      case 'B': return 'bg-stone-50 text-stone-500 border border-stone-200';
      case 'ブロック': return 'bg-red-50 text-red-700 border border-red-200';
      default: return 'bg-stone-100 text-stone-600 border border-stone-200';
    }
  };

  const isFemale = (workerId: string) => parseInt(workerId, 10) % 2 === 0;
  const topComments = getTopComments(worker.id);
  const workerEvals = getWorkerEvaluations(worker.id);
  const showChart = worker.rank !== 'ブロック' && workerEvals.length > 0;
  const mainBodyText = getMainBodyText();
  const benefitsIcons = getBenefitsIcons();

  const toggleSection = (section: string) => {
    setExpandedSection(expandedSection === section ? null : section);
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* ヘッダー */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            to={`/mock-workers/${id}`}
            className="flex items-center gap-1.5 px-3 py-2 text-stone-500 hover:text-amber-700 hover:bg-amber-50 rounded-lg transition-all text-sm whitespace-nowrap cursor-pointer shrink-0"
          >
            <i className="ri-arrow-left-line"></i>
            <span className="hidden sm:inline">詳細ページへ戻る</span>
            <span className="sm:hidden">戻る</span>
          </Link>
          <div className="h-5 w-px bg-stone-200 shrink-0"></div>
          <h1 className="text-sm sm:text-lg font-semibold text-stone-800 truncate">オファーメール送信（モック）</h1>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5 sm:py-8 space-y-4 sm:space-y-6">

        {/* ワーカー情報カード */}
        <div className="bg-white rounded-2xl border border-stone-200 p-4 sm:p-6">
          <div className="flex items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-5 min-w-0">
              <div className="w-12 h-12 sm:w-16 sm:h-16 bg-amber-50 rounded-xl flex items-center justify-center border border-amber-200 overflow-hidden shrink-0">
                <GenderSilhouette isFemale={isFemale(worker.id)} size={40} />
              </div>
              <div className="min-w-0">
                <h2 className="text-base sm:text-xl font-bold text-stone-800 mb-0.5 sm:mb-1 truncate">{worker.name}</h2>
                <p className="text-xs sm:text-sm text-stone-500 mb-1.5 sm:mb-2 truncate">{worker.email}</p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {worker.qualifications.map((qual, idx) => (
                    <span key={idx} className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded-full border border-amber-200 whitespace-nowrap">{qual}</span>
                  ))}
                </div>
              </div>
            </div>
            <div className={`px-3 sm:px-5 py-1.5 sm:py-2.5 rounded-xl text-base sm:text-xl font-bold whitespace-nowrap shrink-0 ${getRankBadgeStyle(worker.rank)}`}>
              {worker.rank}
            </div>
          </div>
        </div>

        {/* ── HTMLメール送信方法 アコーディオン ── */}
        <div className="rounded-xl border border-amber-200 overflow-hidden">
          {/* アコーディオンヘッダー */}
          <button
            onClick={() => setShowSendInstructions(!showSendInstructions)}
            className="w-full bg-amber-50 px-4 py-3 sm:px-5 sm:py-4 flex items-center justify-between gap-3 cursor-pointer hover:bg-amber-100 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className="ri-mail-send-line text-amber-600 text-base sm:text-lg"></i>
              </div>
              <div className="text-left">
                <p className="text-xs sm:text-sm font-semibold text-amber-800">HTMLメールの送信方法</p>
                {!showSendInstructions && (
                  <p className="text-xs text-amber-600 mt-0.5">タップして手順を確認</p>
                )}
              </div>
            </div>
            <i className={`ri-arrow-${showSendInstructions ? 'up' : 'down'}-s-line text-xl text-amber-500 shrink-0`}></i>
          </button>

          {/* アコーディオン本体 */}
          {showSendInstructions && (
            <div className="bg-white border-t border-amber-100">
              <div className="px-4 sm:px-6 py-4 sm:py-5">
                {/* 概要説明 */}
                <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-5">
                  <p className="text-xs text-amber-700 leading-relaxed">
                    ①HTMLをコピー → ②メーラーを開く → ③本文欄に貼り付けて送信、の3ステップで送れます。<br />
                    メーラーの本文欄に貼り付け（Ctrl+V / ⌘+V）してから送信してください。
                  </p>
                </div>

                {/* ステップ */}
                <div className="flex flex-col gap-3 mb-5 sm:mb-6">
                  {[
                    { step: '1', icon: 'ri-file-copy-line', color: 'bg-amber-100 text-amber-700', label: 'HTMLをコピー', desc: '下の「① HTMLをコピーする」ボタンを押すと、メール本文全体がクリップボードに保存されます。' },
                    { step: '2', icon: 'ri-mail-open-line', color: 'bg-stone-100 text-stone-600', label: 'メーラーを開く', desc: '「② メーラーを開く」ボタンを押すと、件名・宛先が入力済みの状態でメーラーが起動します。' },
                    { step: '3', icon: 'ri-clipboard-line', color: 'bg-green-100 text-green-700', label: '本文に貼り付けて送信', desc: 'メーラーの本文欄をクリックし、Ctrl+V（Mac: ⌘+V）で貼り付けてから送信してください。' },
                  ].map(({ step, icon, color, label, desc }) => (
                    <div key={step} className="flex items-start gap-3 sm:gap-4">
                      <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-full ${color} flex items-center justify-center shrink-0 font-bold text-sm`}>
                        {step}
                      </div>
                      <div className="flex-1 pt-0.5">
                        <div className="flex items-center gap-2 mb-0.5">
                          <i className={`${icon} text-stone-400 text-sm`}></i>
                          <span className="text-sm font-semibold text-stone-700">{label}</span>
                        </div>
                        <p className="text-xs text-stone-500 leading-relaxed">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* コピーボタン */}
                <button
                  onClick={handleCopyHtml}
                  className={`w-full flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3.5 sm:py-4 rounded-xl text-sm sm:text-base font-bold transition-all shadow-sm whitespace-nowrap cursor-pointer mb-3 ${
                    copyStatus === 'copied'
                      ? 'bg-green-600 text-white shadow-green-200'
                      : 'bg-amber-700 text-white hover:bg-amber-800 shadow-amber-200'
                  }`}
                >
                  {copyStatus === 'copied' ? (
                    <>
                      <i className="ri-check-double-line text-lg sm:text-xl"></i>
                      <span className="text-center leading-snug">
                        HTMLコピー完了！<br className="sm:hidden" />
                        <span className="hidden sm:inline">　</span>次はメーラーを開いて貼り付けてください
                      </span>
                    </>
                  ) : (
                    <>
                      <i className="ri-file-copy-2-line text-lg sm:text-xl"></i>
                      ① HTMLをコピーする
                    </>
                  )}
                </button>

                {/* メーラーを開くボタン */}
                <button
                  onClick={handleOpenMailer}
                  className="w-full flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-3.5 rounded-xl text-sm font-semibold border-2 border-stone-300 text-stone-600 hover:border-amber-400 hover:text-amber-700 hover:bg-amber-50 transition-all whitespace-nowrap cursor-pointer mb-4"
                >
                  <i className="ri-mail-send-line text-base sm:text-lg"></i>
                  ② メーラーを開く（件名・宛先入力済み）
                </button>

                {/* Gmail セクション */}
                <div className="border-t border-stone-100 pt-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="h-px flex-1 bg-stone-100"></div>
                    <span className="text-xs text-stone-400 px-2 font-medium whitespace-nowrap">Gmail をお使いの方はこちら</span>
                    <div className="h-px flex-1 bg-stone-100"></div>
                  </div>
                  <div className="bg-red-50 border border-red-100 rounded-xl px-3 sm:px-4 py-3 mb-3">
                    <div className="flex items-start gap-2 mb-2">
                      <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-information-line text-red-500 text-sm"></i>
                      </div>
                      <p className="text-xs font-semibold text-red-700">GmailはCtrl+Vで貼り付けるとHTMLが崩れます</p>
                    </div>
                    <ol className="text-xs text-red-600 leading-relaxed space-y-1 ml-7 list-decimal">
                      <li>下の「Gmail用HTMLをダウンロード」ボタンを押す</li>
                      <li>ダウンロードされた <strong>.html ファイル</strong>をブラウザ（Chrome等）で開く</li>
                      <li>全選択（Ctrl+A / ⌘+A）→ コピー（Ctrl+C / ⌘+C）</li>
                      <li>Gmailの作成画面の本文欄に貼り付け（Ctrl+V / ⌘+V）</li>
                      <li>HTMLメールとして正しく表示されます ✓</li>
                    </ol>
                  </div>
                  <button
                    onClick={handleDownloadForGmail}
                    className="w-full flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-3.5 rounded-xl text-sm font-semibold bg-red-500 text-white hover:bg-red-600 transition-all whitespace-nowrap cursor-pointer shadow-sm"
                  >
                    <i className="ri-download-2-line text-base sm:text-lg"></i>
                    Gmail用HTMLをダウンロード
                  </button>
                </div>

                {/* コピー完了フィードバック */}
                {copyStatus === 'copied' && (
                  <div className="mt-4 bg-green-50 border border-green-200 rounded-xl px-3 sm:px-4 py-3 flex items-start gap-2 sm:gap-3">
                    <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-line text-green-600 text-base"></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-green-800 mb-0.5">クリップボードにコピーしました</p>
                      <p className="text-xs text-green-700 leading-relaxed">
                        メーラーを開いて、本文欄に <strong>Ctrl+V（Mac: ⌘+V）</strong> で貼り付けてから送信してください。<br />
                        HTMLメールとして正しく表示されます。
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* メールプレビュー */}
        <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
          <div className="bg-stone-50 border-b border-stone-100 px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-2">
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className="ri-mail-line text-stone-500 text-base"></i>
            </div>
            <span className="text-sm font-semibold text-stone-700">メールプレビュー</span>
          </div>
          <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-2">
            <div className="flex gap-2 text-xs sm:text-sm">
              <span className="text-stone-400 shrink-0 w-10">件名</span>
              <span className="text-stone-700 font-medium">{getEmailSubject()}</span>
            </div>
            <div className="flex gap-2 text-xs sm:text-sm">
              <span className="text-stone-400 shrink-0 w-10">宛先</span>
              <span className="text-stone-700">{worker.name} 様（{worker.email}）</span>
            </div>
            <div className="border-t border-stone-100 pt-3 mt-2">
              {mainBodyText.map((t, i) => (
                <p key={i} className="text-xs text-stone-600 leading-relaxed mb-2">{t}</p>
              ))}
            </div>
          </div>
        </div>

        {/* レーダーチャート（ランクがブロック以外かつ評価あり） */}
        {showChart && (
          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
            <div className="bg-stone-50 border-b border-stone-100 px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className="ri-radar-line text-amber-600 text-base"></i>
              </div>
              <span className="text-sm font-semibold text-stone-700">評価レーダーチャート</span>
            </div>
            <div className="px-4 sm:px-6 py-4 sm:py-5">
              <RadarChart evaluations={workerEvals} />
            </div>

            {/* 各評価項目の説明 */}
            <div className="border-t border-stone-100 px-4 sm:px-6 py-4 sm:py-5">
              <p className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">評価項目の説明</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {CATEGORIES.map((cat, idx) => {
                  const latestEval = [...workerEvals]
                    .sort((a, b) => new Date(b.work_date).getTime() - new Date(a.work_date).getTime())[0];
                  const latestScore = latestEval ? (latestEval as any)[cat.key] as number : null;
                  return (
                    <div key={idx} className="flex items-start gap-3 px-3 py-2.5 bg-stone-50 rounded-xl border border-stone-100">
                      <div className="w-7 h-7 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-amber-700">{idx + 1}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-0.5">
                          <span className="text-xs font-bold text-stone-700">{cat.label}</span>
                          {latestScore !== null && (
                            <span className={`text-xs font-bold shrink-0 ${latestScore >= 8 ? 'text-amber-600' : latestScore >= 5 ? 'text-stone-600' : 'text-red-500'}`}>
                              {latestScore}<span className="font-normal text-stone-400">/10</span>
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-stone-500 leading-relaxed">{cat.description}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* スタッフコメント */}
        {topComments.length > 0 && worker.rank !== 'ブロック' && (
          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
            <div className="bg-stone-50 border-b border-stone-100 px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className="ri-chat-quote-line text-amber-600 text-base"></i>
              </div>
              <span className="text-sm font-semibold text-stone-700">スタッフコメント（メール内に含まれます）</span>
            </div>
            <div className="px-4 sm:px-6 py-4 sm:py-5">
              {topComments.map((c, idx) => (
                <div key={idx} className="flex items-end gap-3 mb-4 last:mb-0">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-stone-700 text-xs font-bold shrink-0 border border-amber-200">
                    {c.evaluator.charAt(0)}
                  </div>
                  <div className="flex flex-col gap-1 max-w-xs sm:max-w-sm">
                    <span className="text-xs text-stone-400 ml-1">{c.evaluator}さん</span>
                    <div className="bg-stone-50 border border-stone-200 rounded-2xl rounded-bl-sm px-3 py-2.5">
                      <p className="text-xs sm:text-sm text-stone-700 leading-relaxed">「{c.comment}」</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ベネフィットカード（S/Aランクのみ） */}
        {benefitsIcons && (
          <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
            <div className="bg-amber-50 border-b border-amber-100 px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-2">
              <div className="w-5 h-5 flex items-center justify-center shrink-0">
                <i className="ri-gift-line text-amber-600 text-base"></i>
              </div>
              <span className="text-sm font-semibold text-amber-800">
                {worker.rank === 'S' ? '正社員オファー内容' : '直接スポットのメリット'}
              </span>
            </div>
            <div className="px-4 sm:px-6 py-4 sm:py-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {benefitsIcons.map((b, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-amber-50 rounded-xl border border-amber-100">
                  <div className="w-8 h-8 flex items-center justify-center shrink-0">
                    <i className={`${b.icon} text-amber-600 text-lg`}></i>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-stone-800 mb-0.5">{b.title}</p>
                    <p className="text-xs text-stone-500 leading-relaxed">{b.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ワーカー詳細・送信対象 アコーディオン */}
        <div className="space-y-2 sm:space-y-3">
          {[
            { key: 'worker', icon: 'ri-user-line', label: 'ワーカー詳細情報' },
            { key: 'target', icon: 'ri-send-plane-line', label: '送信対象' },
          ].map(({ key, icon, label }) => (
            <div key={key} className="bg-white rounded-xl border border-stone-200 overflow-hidden">
              <button
                onClick={() => toggleSection(key)}
                className="w-full flex items-center justify-between px-4 sm:px-5 py-3.5 sm:py-4 hover:bg-stone-50 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2 sm:gap-3">
                  <i className={`${icon} text-amber-600 text-base sm:text-lg`}></i>
                  <span className="text-sm font-semibold text-stone-700">{label}</span>
                </div>
                <i className={`ri-arrow-${expandedSection === key ? 'up' : 'down'}-s-line text-xl text-stone-400`}></i>
              </button>
              {expandedSection === key && (
                <div className="px-4 sm:px-5 py-3.5 sm:py-4 border-t border-stone-100 bg-stone-50">
                  {key === 'worker' ? (
                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                      <div>
                        <div className="text-xs text-stone-400 mb-1">平均スコア</div>
                        <div className="text-base sm:text-lg font-bold text-amber-700">{worker.averageScore.toFixed(1)}点</div>
                      </div>
                      <div>
                        <div className="text-xs text-stone-400 mb-1">ランク</div>
                        <div className="text-base sm:text-lg font-bold text-stone-700">{worker.rank}ランク</div>
                      </div>
                      <div>
                        <div className="text-xs text-stone-400 mb-1">評価回数</div>
                        <div className="text-sm text-stone-700">{workerEvals.length}回</div>
                      </div>
                      <div>
                        <div className="text-xs text-stone-400 mb-1">資格</div>
                        <div className="text-xs sm:text-sm text-stone-700">{worker.qualifications.join(', ')}</div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-3 bg-white rounded-lg border border-stone-100">
                      <i className="ri-mail-line text-amber-600 text-lg sm:text-xl"></i>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-stone-700">{worker.name}</div>
                        <div className="text-xs text-stone-500 truncate">{worker.email}</div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="pb-6 sm:pb-8">
          <Link
            to={`/mock-workers/${id}`}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-white border border-stone-200 text-stone-500 text-sm font-medium rounded-xl hover:bg-stone-50 transition-colors whitespace-nowrap w-full"
          >
            <i className="ri-arrow-left-line"></i>
            詳細ページへ戻る
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MockWorkerOfferPage;
